import {
    HOLE_PENALTIES, HOLE_PENALTY_LABELS, HOLE_PENALTY_RULES, holePenaltyStrokes,
} from '@golf/common';
import type { HolePenalty } from '@golf/common';
import { Skull, X } from 'lucide-react';

/* Les pénalités qui n'appartiennent à aucun coup.
 *
 * ── L'un des deux boutons du coup ───────────────────────────────────────────
 *
 * Toucher le sable en entrant dans un bunker n'est pas un coup : cela n'a ni
 * club ni zone d'arrivée. La carte du coup pose donc deux questions et deux
 * seulement — « j'ai frappé », ou « il s'est passé autre chose » — et ce bouton
 * porte la seconde.
 *
 * Il ouvre une feuille qui se referme SUR PLACE. Rien ne suit : on n'a pas
 * frappé, donc il n'y a aucun coup à commenter derrière, aucune zone à
 * désigner, aucun club à reprendre.
 *
 * ── Le barème n'est pas demandé au joueur ───────────────────────────────────
 *
 * On nomme ce qui s'est passé, l'application compte. Personne ne se souvient
 * sur le parcours que le sable vaut deux coups et la balle déplacée un seul —
 * et un écran qui pose la question obtiendrait surtout des réponses fausses.
 * La règle est citée sous chaque motif : c'est ce qu'on veut pouvoir montrer à
 * son partenaire quand il conteste.
 */
export function BoutonPenalite({ nombre, onOuvrir }: {
    nombre: number;
    onOuvrir: () => void;
}) {
    return (
        /* Le SECOND des deux boutons du coup, et non plus une ligne discrète en
           bas de carte. Il répond à « il s'est passé quelque chose, mais je n'ai
           pas frappé » : sable touché, balle déplacée, conseil demandé. À côté
           de « Balle frappée », les deux cas de figure du golf sont couverts.

           En brique pleine dès qu'une pénalité est posée, en contour tant qu'il
           n'y en a pas : un bouton qui compte doit se voir compter. */
        <button type="button" onClick={onOuvrir}
            className="flex w-full items-center justify-center gap-2 rounded-field border-2
                       px-3 py-3 text-sm font-bold transition-transform active:scale-[0.99]"
            style={{
                borderColor: 'var(--brique-bord)',
                backgroundColor: nombre > 0 ? 'var(--brique)' : 'transparent',
                color: nombre > 0 ? 'var(--brique-encre)' : 'var(--brique-bord)',
            }}>
            {/* Une TÊTE DE MORT, et non plus un maillet de juge. Le maillet
                disait la sanction, ce qui est vrai mais froid ; le crâne dit
                ce que le joueur pense en la notant. */}
            <Skull size={16} aria-hidden="true" />
            {nombre > 0 ? `Pénalités du trou : +${nombre}` : 'Pénalité'}
        </button>
    );
}

/** La feuille de choix. Une ligne par infraction, son coût à droite. */
export function FeuillePenalite({ posees, onAjouter, onRetirer, onFermer }: {
    posees: HolePenalty[];
    onAjouter: (r: HolePenalty) => void;
    onRetirer: (index: number) => void;
    onFermer: () => void;
}) {
    return (
        <div className="modal modal-open" role="dialog"
            onClick={(e) => { if (e.target === e.currentTarget) onFermer(); }}>
            <div className="modal-box max-w-md overflow-hidden p-0">
                <header className="flex items-start gap-3 bg-[var(--fairway-deep)] px-4 py-3.5">
                    <div className="min-w-0 flex-1">
                        <h3 className="font-display text-lg font-bold leading-tight text-white">
                            Pénalité
                        </h3>
                        {/* « En dehors d'un coup » devenait faux : un hors-limites
                            vient bien d'une balle frappée. Ce que ces motifs ont
                            en commun n'est pas leur origine, c'est leur effet. */}
                        <p className="mt-0.5 text-xs font-semibold text-white/60">
                            Des coups en plus sur ce trou
                        </p>
                    </div>
                    <button type="button" onClick={onFermer} aria-label="Fermer"
                        className="-mr-1 shrink-0 rounded-field p-1.5 text-white/70 transition-colors hover:bg-white/10 hover:text-white">
                        <X size={18} />
                    </button>
                </header>

                {/* Ce qui est DÉJÀ posé, en premier et retirable d'un tap.
                    Une pénalité s'ajoute d'un geste, elle doit se retirer du
                    même : on se trompe de ligne, ou l'on découvre au trou
                    suivant qu'il n'y avait pas faute. */}
                {posees.length > 0 && (
                    <ul className="space-y-1.5 border-b border-base-300 p-2">
                        {posees.map((r, i) => (
                            <li key={`${r}-${i}`}
                                className="flex items-center gap-2 rounded-field px-2.5 py-2"
                                style={{ backgroundColor: 'var(--brique)', color: 'var(--brique-encre)' }}>
                                <span className="font-score shrink-0 text-lg leading-none">
                                    +{holePenaltyStrokes(r)}
                                </span>
                                <span className="min-w-0 flex-1 truncate text-sm font-bold">
                                    {HOLE_PENALTY_LABELS[r]}
                                </span>
                                <button type="button" onClick={() => onRetirer(i)}
                                    aria-label={`Retirer ${HOLE_PENALTY_LABELS[r]}`}
                                    className="shrink-0 rounded-field p-1.5 transition-colors hover:bg-black/10">
                                    <X size={16} />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}

                <ul className="max-h-[55dvh] space-y-1 overflow-y-auto p-2">
                    {HOLE_PENALTIES.map((r) => (
                        <li key={r}>
                            <button type="button" onClick={() => onAjouter(r)}
                                className="flex w-full items-center gap-3 rounded-field px-2.5 py-2 text-left transition-colors hover:bg-base-200">
                                <span className="font-score w-7 shrink-0 text-center text-lg leading-none"
                                    style={{ color: 'var(--brique-bord)' }}>
                                    +{holePenaltyStrokes(r)}
                                </span>
                                <span className="min-w-0 flex-1">
                                    <span className="block text-sm font-bold">{HOLE_PENALTY_LABELS[r]}</span>
                                    <span className="block text-xs opacity-60">{HOLE_PENALTY_RULES[r]}</span>
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}
